import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';

const TwoFactorSetupPage = () => {
  const navigate    = useNavigate();
  const { complete2faSetup } = useAuth();

  // Read setup_token from sessionStorage (placed there by LoginPage after login)
  const setupToken  = sessionStorage.getItem('setup_token') || '';
  const emailHint   = sessionStorage.getItem('setup_email_hint') || '';

  // If no setup_token, redirect to login
  useEffect(() => {
    if (!setupToken) navigate('/login', { replace: true });
  }, [setupToken, navigate]);

  // ── step: 'email' | 'qr' | 'done'
  const [step, setStep]           = useState('email');

  // Email OTP step
  const [emailCode, setEmailCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const emailRef = useRef(null);

  // QR step
  const [setupData, setSetupData] = useState(null);   // {qr_svg, manual_key, account}
  const [qrLoading, setQrLoading] = useState(false);
  const [totp, setTotp]           = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [copied, setCopied]       = useState(false);
  const totpRef = useRef(null);

  // Auto-focus email input on mount
  useEffect(() => {
    setTimeout(() => emailRef.current?.focus(), 100);
  }, []);

  // Auto-focus TOTP input when QR step loads
  useEffect(() => {
    if (step === 'qr') setTimeout(() => totpRef.current?.focus(), 100);
  }, [step]);

  // Auto-submit email OTP at 6 digits
  useEffect(() => {
    if (emailCode.length === 6 && !emailLoading) handleEmailVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailCode]);

  // Auto-submit TOTP at 6 digits
  useEffect(() => {
    if (totp.length === 6 && !totpLoading) handleTotpActivate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totp]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── Fetch QR data (shared between initial load and retry) ────────────────
  const fetchQrData = async () => {
    setQrLoading(true);
    try {
      const r = await authService.get2faSetup(setupToken);
      setSetupData(r.data);
      setStep('qr');
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not load QR code. Try again.';
      setEmailError(msg);
      // If session expired send back to login
      if (err.response?.status === 401) {
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    } finally {
      setQrLoading(false);
    }
  };

  // ── Step 1: verify email OTP ─────────────────────────────────────────────
  const handleEmailVerify = async (e) => {
    if (e) e.preventDefault();
    const code = emailCode.trim();
    if (code.length < 6) return;
    setEmailError('');
    setEmailLoading(true);
    try {
      await authService.verifyEmailOtp(setupToken, code);
      // Email verified — fetch QR (step switches inside fetchQrData only on success)
      await fetchQrData();
    } catch (err) {
      const status = err.response?.status;
      const msg = status >= 500
        ? 'Server error — please try again in a moment.'
        : (err.response?.data?.error || 'Invalid code. Try again.');
      setEmailError(msg);
      setEmailCode('');
      setTimeout(() => emailRef.current?.focus(), 50);
      if (err.response?.status === 401 || err.response?.status === 429) {
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await authService.resendEmailOtp(setupToken);
      setResendCooldown(60);
      setEmailCode('');
      setEmailError('');
      setTimeout(() => emailRef.current?.focus(), 50);
    } catch (err) {
      setEmailError(err.response?.data?.error || 'Could not resend. Please try again.');
    }
  };

  // ── Step 2: verify TOTP + get real token ─────────────────────────────────
  const handleTotpActivate = async (e) => {
    if (e) e.preventDefault();
    const code = totp.trim();
    if (code.length < 6) return;
    setTotpError('');
    setTotpLoading(true);
    try {
      const r = await authService.enable2fa(setupToken, code);
      complete2faSetup(r.data.token, r.data.user);
      setStep('done');
    } catch (err) {
      const status = err.response?.status;
      setTotpError(
        status >= 500
          ? 'Server error — please try again in a moment.'
          : (err.response?.data?.error || 'Invalid code. Check your authenticator app.')
      );
      setTotp('');
      setTimeout(() => totpRef.current?.focus(), 50);
    } finally {
      setTotpLoading(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(setupData?.manual_key || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Shared shell ─────────────────────────────────────────────────────────
  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-8 pt-8 pb-6 border-b border-white/8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#c9a96e]/20 flex items-center justify-center text-[#c9a96e] font-bold text-sm">🔐</div>
            <span className="text-[10px] uppercase tracking-[3px] text-[#c9a96e] font-medium">Security Setup Required</span>
          </div>
          <h1 className="font-serif text-2xl text-white mb-1">Set up Two-Factor Authentication</h1>
          <p className="text-white/45 text-sm leading-relaxed">
            All Maghaweer.AI accounts require 2FA. Complete the steps below to secure your account.
          </p>
        </div>
        <div className="px-8 py-6 space-y-6">
          {children}
        </div>
      </div>
    </div>
  );

  // ── Done ─────────────────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-900/30 border border-green-700/40 flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl text-green-400">✓</span>
          </div>
          <h2 className="font-serif text-2xl text-white mb-2">2FA Enabled</h2>
          <p className="text-white/50 text-sm mb-8">
            Your account is now protected with two-factor authentication.<br/>
            You'll need your authenticator app on every login.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 rounded-lg text-xs uppercase tracking-[3px] font-semibold bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#dfc08a] transition-colors"
          >
            Enter Platform
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: QR ──────────────────────────────────────────────────────────
  if (step === 'qr') {
    return (
      <Shell>
        {/* Progress */}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-green-700/40 border border-green-600/40 text-green-400 text-[10px] font-bold flex items-center justify-center shrink-0">✓</span>
            <span className="text-xs text-white/30 line-through">Email verified</span>
          </div>
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] uppercase tracking-[2px] text-[#c9a96e] font-medium">Step 2 of 2</span>
        </div>

        {qrLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#c9a96e] border-t-transparent" />
          </div>
        ) : !setupData ? (
          /* Data failed to load — show retry */
          <div className="text-center py-8 space-y-4">
            <p className="text-white/40 text-sm">Could not load QR code.</p>
            <button
              onClick={fetchQrData}
              className="px-4 py-2 rounded-lg text-xs uppercase tracking-[2px] border border-[#c9a96e]/40 text-[#c9a96e] hover:bg-[#c9a96e]/10 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Step 1: Scan */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-[#c9a96e]/20 text-[#c9a96e] text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
                <span className="text-xs uppercase tracking-[2px] text-white/50 font-medium">Scan QR Code</span>
              </div>
              {setupData?.qr_svg ? (
                <div
                  className="flex justify-center p-4 bg-white rounded-xl mx-auto w-fit"
                  dangerouslySetInnerHTML={{ __html: setupData.qr_svg }}
                  style={{ maxWidth: 200 }}
                />
              ) : (
                <div className="h-48 bg-white/5 rounded-xl flex items-center justify-center text-white/30 text-sm border border-white/10">
                  QR unavailable — use manual key below
                </div>
              )}
              <div className="mt-3">
                <p className="text-[10px] text-white/35 mb-1.5 uppercase tracking-[1px]">Or enter manually:</p>
                <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 border border-white/10">
                  <code className="flex-1 text-xs text-[#c9a96e] font-mono tracking-widest break-all">
                    {setupData?.manual_key}
                  </code>
                  <button onClick={copyKey} className="shrink-0 text-[10px] text-white/40 hover:text-white/70 transition-colors">
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            {/* Step 2: Verify TOTP */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-[#c9a96e]/20 text-[#c9a96e] text-[10px] font-bold flex items-center justify-center shrink-0">2</span>
                <span className="text-xs uppercase tracking-[2px] text-white/50 font-medium">Enter 6-Digit Code to Verify</span>
              </div>
              {totpError && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-red-900/20 border border-red-800/30 text-red-400 text-xs">
                  {totpError}
                </div>
              )}
              <form onSubmit={handleTotpActivate}>
                <input
                  ref={totpRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totp}
                  onChange={e => setTotp(e.target.value.replace(/[^0-9]/g, ''))}
                  autoFocus
                  placeholder="000000"
                  autoComplete="one-time-code"
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-2xl text-center tracking-[0.6em] text-white font-mono outline-none focus:border-[#c9a96e]/50 placeholder-white/20 mb-4"
                />
                <button
                  type="submit"
                  disabled={totpLoading || totp.length < 6}
                  className="w-full py-3 rounded-lg text-xs uppercase tracking-[3px] font-semibold transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#dfc08a]"
                >
                  {totpLoading ? 'ACTIVATING...' : 'ACTIVATE 2FA'}
                </button>
              </form>
            </div>

            <p className="text-[10px] text-white/20 leading-relaxed text-center">
              Codes refresh every 30 seconds. Make sure your device clock is synced.
            </p>
          </>
        )}
      </Shell>
    );
  }

  // ── Step 1: Email OTP ────────────────────────────────────────────────────
  return (
    <Shell>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="w-5 h-5 rounded-full bg-[#c9a96e]/20 text-[#c9a96e] text-[10px] font-bold flex items-center justify-center shrink-0">1</span>
          <span className="text-xs uppercase tracking-[2px] text-white/50 font-medium">Verify your email</span>
        </div>
        <p className="text-white/40 text-sm mt-2 mb-5 leading-relaxed">
          We sent a 6-digit code to{' '}
          <span className="text-white/70 font-mono">{emailHint || 'your email'}</span>.
          Enter it below to continue.
        </p>

        {emailError && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-red-900/20 border border-red-800/30 text-red-400 text-xs">
            {emailError}
          </div>
        )}

        <form onSubmit={handleEmailVerify}>
          <input
            ref={emailRef}
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={emailCode}
            onChange={e => setEmailCode(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="000000"
            autoComplete="one-time-code"
            className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-2xl text-center tracking-[0.6em] text-white font-mono outline-none focus:border-[#c9a96e]/50 placeholder-white/20 mb-4"
          />
          <button
            type="submit"
            disabled={emailLoading || emailCode.length < 6}
            className="w-full py-3 rounded-lg text-xs uppercase tracking-[3px] font-semibold transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed bg-[#c9a96e] text-[#0a0a0a] hover:bg-[#dfc08a]"
          >
            {emailLoading ? 'VERIFYING...' : 'VERIFY EMAIL'}
          </button>
        </form>

        <button
          onClick={handleResend}
          disabled={resendCooldown > 0}
          className="mt-4 text-xs text-white/30 hover:text-white/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-center w-full"
        >
          {resendCooldown > 0
            ? `Resend code in ${resendCooldown}s`
            : "Didn't receive it? Resend"}
        </button>
      </div>

      <p className="text-[10px] text-white/20 leading-relaxed text-center">
        Step 1 of 2 — email verification. Next: scan a QR code with your authenticator app.
      </p>
    </Shell>
  );
};

export default TwoFactorSetupPage;
